import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icons } from '../icons/Icons';
import { useAuth } from '../../context/AuthContext';
import { useUserSettings } from '../../context/UserSettingsContext';
import { getImportProvider, importProviders } from '../../integrations/importProviders';
import {
  clearRecentImports,
  getRecentImports,
  saveRecentImport
} from '../../services/recentImports';

const formatDate = (value) => {
  if (!value) return '';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'short',
    timeStyle: 'short'
  }).format(new Date(value));
};

const createImportEvent = (file) => ({
  target: {
    files: [file],
    value: null
  }
});

const isProviderReady = (provider, config = {}) => {
  if (!config.enabled) return false;
  if (provider.id === 'google_drive') return Boolean(config.folderId);
  if (provider.id === 'mega') return Boolean(config.folderPath || config.folderName);
  return true;
};

const getProviderIcon = (providerId) => {
  if (providerId === 'google_drive') return Icons.GoogleDrive;
  if (providerId === 'mega') return Icons.Mega;
  return Icons.FileSpreadsheet;
};

const ImportProjectModal = ({ onClose, onImport, t }) => {
  const { isAuthenticated, user } = useAuth();
  const { settings: userSettings } = useUserSettings();
  const fileInputRef = useRef(null);
  const integrations = useMemo(() => userSettings?.integrations || {}, [userSettings?.integrations]);
  const enabledProviders = useMemo(
    () => importProviders.filter(provider => isProviderReady(provider, integrations[provider.id])),
    [integrations]
  );
  const enabledProviderIds = useMemo(
    () => new Set(enabledProviders.map(provider => provider.id)),
    [enabledProviders]
  );
  const [selectedProviderId, setSelectedProviderId] = useState(enabledProviders[0]?.id || '');
  const [providerFiles, setProviderFiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [recentImports, setRecentImports] = useState(() => getRecentImports());
  const [credentialsByProvider, setCredentialsByProvider] = useState({});
  const [isLoadingProvider, setIsLoadingProvider] = useState(false);
  const [importingId, setImportingId] = useState(null);
  const [error, setError] = useState('');
  const [isProviderPanelVisible, setIsProviderPanelVisible] = useState(false);

  const selectedProvider = getImportProvider(selectedProviderId);
  const SelectedProviderIcon = getProviderIcon(selectedProviderId);
  const selectedProviderConfig = integrations[selectedProviderId] || {};
  const visibleRecentImports = recentImports.filter(entry => {
    const provider = entry.provider || entry.source;
    return provider === 'local' || enabledProviderIds.has(provider);
  });
  const filteredProviderFiles = providerFiles.filter(file =>
    (file.name || '').toLowerCase().includes(searchTerm.trim().toLowerCase())
  );

  useEffect(() => {
    setRecentImports(getRecentImports());
  }, []);

  useEffect(() => {
    if (!selectedProviderId && enabledProviders[0]?.id) {
      setSelectedProviderId(enabledProviders[0].id);
    } else if (selectedProviderId && !enabledProviderIds.has(selectedProviderId)) {
      setSelectedProviderId(enabledProviders[0]?.id || '');
    }
  }, [enabledProviderIds, enabledProviders, selectedProviderId]);

  useEffect(() => {
    setProviderFiles([]);
    setSearchTerm('');
    setError('');
  }, [selectedProviderId]);

  const importFile = (file, recentEntry) => {
    const nextRecent = saveRecentImport(recentEntry);
    setRecentImports(nextRecent);
    onImport(createImportEvent(file));
    onClose();
  };

  const handleLocalFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    importFile(file, {
      source: 'local',
      provider: 'local',
      providerName: t('landing.localFile'),
      name: file.name,
      size: file.size
    });
  };

  const updateCredential = (providerId, field, value) => {
    setCredentialsByProvider(prev => ({
      ...prev,
      [providerId]: {
        ...(prev[providerId] || {}),
        [field]: value
      }
    }));
  };

  const handleLoadProviderFiles = async ({ forceRefresh = false } = {}) => {
    if (!selectedProvider) return;

    setIsProviderPanelVisible(true);
    setError('');
    setIsLoadingProvider(true);

    try {
      if (!selectedProvider.isConfigured()) {
        throw new Error(selectedProvider.notConfiguredMessage || 'Integrazione non configurata.');
      }

      if (selectedProvider.connect) {
        await selectedProvider.connect(credentialsByProvider[selectedProvider.id] || {});
      }

      const files = await selectedProvider.listFiles(selectedProviderConfig, {
        forceRefresh,
        userId: user?.id
      });
      setProviderFiles(files);
    } catch (loadError) {
      setError(loadError.message || 'Errore durante il collegamento al provider.');
    } finally {
      setIsLoadingProvider(false);
    }
  };

  const handleProviderImport = async (provider, providerFile) => {
    setError('');
    setImportingId(`${provider.id}:${providerFile.id}`);

    try {
      const file = await provider.downloadFile(providerFile);
      importFile(file, provider.getRecentEntry(providerFile));
    } catch (importError) {
      setError(importError.message || 'Errore durante importazione.');
    } finally {
      setImportingId(null);
    }
  };

  const handleRecentClick = async (entry) => {
    if ((entry.provider || entry.source) === 'local') {
      fileInputRef.current?.click();
      return;
    }

    const provider = getImportProvider(entry.provider || entry.source);
    if (!provider) {
      setError('Provider non disponibile per questo file recente.');
      return;
    }
    if (!enabledProviderIds.has(provider.id)) {
      setError('Questo provider non e abilitato nelle impostazioni.');
      setSelectedProviderId(provider.id);
      setIsProviderPanelVisible(true);
      return;
    }

    try {
      if (provider.connect) {
        await provider.connect(credentialsByProvider[provider.id] || {});
      }

      const providerFile = provider.getFileFromRecent(entry);
      await handleProviderImport(provider, providerFile);
    } catch (recentError) {
      setError(recentError.message || 'Non posso riaprire questo file recente.');
      setSelectedProviderId(provider.id);
      setIsProviderPanelVisible(true);
    }
  };

  const handleClearRecent = () => {
    clearRecentImports();
    setRecentImports([]);
  };

  const showProviderLoadingState = isLoadingProvider && providerFiles.length === 0;

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white w-full max-w-5xl max-h-[90vh] rounded-lg shadow-2xl overflow-hidden flex flex-col"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="bg-gray-950 text-white px-5 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">{t('landing.importDialogTitle')}</h2>
            <p className="text-sm text-gray-300">{t('landing.importDialogSubtitle')}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-white/10" aria-label="Close">
            <Icons.X className="w-6 h-6" />
          </button>
        </div>

        <div
          className={`overflow-y-auto p-5 grid grid-cols-1 gap-5 lg:min-h-0 lg:flex-1 ${
            isProviderPanelVisible ? 'lg:grid-cols-[1fr_1.25fr]' : 'lg:grid-cols-1'
          }`}
        >
          <section className="space-y-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">{t('landing.importSources')}</h3>
              <div className="space-y-3">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-blue-600 text-white rounded-md font-semibold hover:bg-blue-700"
                >
                  <Icons.Upload className="w-5 h-5" />
                  {t('landing.importFromComputer')}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleLocalFileChange}
                  accept=".xlsx,.xls"
                  className="hidden"
                />

                {isAuthenticated && enabledProviders.length === 0 && (
                  <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
                    {t('landing.noEnabledProviders')}
                  </p>
                )}

                {!isAuthenticated && (
                  <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
                    {t('landing.loginForExternalImports')}
                  </p>
                )}

                {isAuthenticated && enabledProviders.length > 0 && (
                  <div className="border border-gray-200 rounded-md overflow-hidden">
                    <div className="flex border-b border-gray-200 bg-gray-50">
                      {enabledProviders.map(provider => {
                        const ProviderIcon = getProviderIcon(provider.id);
                        return (
                          <button
                            key={provider.id}
                            onClick={() => setSelectedProviderId(provider.id)}
                            className={`flex-1 px-3 py-2 text-sm font-semibold ${selectedProviderId === provider.id
                              ? 'bg-white text-blue-700'
                              : 'text-gray-600 hover:text-gray-900'
                              }`}
                          >
                            <span className="inline-flex items-center gap-2">
                              <ProviderIcon className="w-4 h-4 shrink-0" />
                              {provider.name}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    <div className="p-3 space-y-3">
                      {selectedProvider?.requiresCredentials && (
                        <div className="space-y-2">
                          {selectedProvider.credentialFields.map(field => (
                            <div key={field.id}>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                {field.label}
                              </label>
                              <input
                                type={field.type}
                                value={credentialsByProvider[selectedProvider.id]?.[field.id] || ''}
                                onChange={(event) => updateCredential(selectedProvider.id, field.id, event.target.value)}
                                autoComplete={field.autoComplete}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                          ))}
                          {selectedProvider.credentialsNote && (
                            <p className="text-xs text-gray-500">{selectedProvider.credentialsNote}</p>
                          )}
                        </div>
                      )}

                      <button
                        onClick={handleLoadProviderFiles}
                        disabled={isLoadingProvider || !selectedProvider?.isConfigured()}
                        className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-gray-900 text-white rounded-md font-semibold hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoadingProvider ? (
                          <Icons.Loader className="w-5 h-5 animate-spin" />
                        ) : (
                          <SelectedProviderIcon className="w-5 h-5" />
                        )}
                        {isLoadingProvider ? selectedProvider?.loadingLabel : selectedProvider?.connectLabel}
                      </button>

                      {selectedProvider && !selectedProvider.isConfigured() && (
                        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
                          {selectedProvider.notConfiguredMessage}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between gap-3 mb-3">
                <h3 className="font-semibold text-gray-900">{t('landing.recentImports')}</h3>
                {recentImports.length > 0 && (
                  <button onClick={handleClearRecent} className="text-sm text-gray-500 hover:text-gray-900">
                    {t('landing.clearRecent')}
                  </button>
                )}
              </div>

              {visibleRecentImports.length === 0 ? (
                <p className="text-sm text-gray-500">{t('landing.noRecentImports')}</p>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                  {visibleRecentImports.map((entry) => (
                    <button
                      key={`${entry.provider || entry.source}:${entry.fileId || entry.name}:${entry.importedAt}`}
                      onClick={() => handleRecentClick(entry)}
                      className="w-full text-left px-3 py-2 border border-gray-200 rounded-md hover:bg-gray-50"
                    >
                      <div className="font-medium text-gray-900 truncate">{entry.name}</div>
                      <div className="text-xs text-gray-500">
                        {entry.providerName || entry.provider || entry.source} - {formatDate(entry.importedAt)}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </section>

          {isProviderPanelVisible && (
          <section className="border border-gray-200 rounded-lg overflow-hidden flex flex-col min-h-0 self-stretch lg:max-h-[calc(90vh-14rem)]">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">
                {selectedProvider?.fileListTitle || t('landing.providerFiles')}
              </h3>
              {providerFiles.length > 0 && (
                <button
                  onClick={() => handleLoadProviderFiles({ forceRefresh: true })}
                  disabled={isLoadingProvider}
                  className="inline-flex items-center gap-2 text-sm text-blue-700 hover:text-blue-900 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoadingProvider && <Icons.Loader className="w-4 h-4 animate-spin" />}
                  {isLoadingProvider ? t('landing.refreshing') : t('landing.refresh')}
                </button>
              )}
            </div>

            {error && (
              <div className="m-4 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
                {error}
              </div>
            )}

            {providerFiles.length > 0 && (
              <div className="p-3 border-b border-gray-100">
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder={t('landing.searchFiles')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}

            {showProviderLoadingState ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-gray-500">
                <Icons.Loader className="w-10 h-10 mx-auto mb-3 text-blue-600 animate-spin" />
                <div>
                  <p className="font-medium text-gray-700">{selectedProvider?.scanningLabel || t('landing.driveScanning')}</p>
                  <p className="text-sm text-gray-500 mt-1">{selectedProvider?.loadingLabel || t('landing.loadingDrive')}</p>
                </div>
              </div>
            ) : providerFiles.length === 0 ? (
              <div className="flex-1 flex items-center justify-center p-8 text-center text-gray-500">
                <div>
                  <SelectedProviderIcon className="w-10 h-10 mx-auto mb-3 text-gray-400" />
                  <p>{enabledProviders.length === 0 ? t('landing.providerNotConfiguredEmpty') : selectedProvider?.emptyState || t('landing.providerEmptyState')}</p>
                </div>
              </div>
            ) : (
              <div className="flex-1 min-h-0 divide-y divide-gray-100 overflow-y-auto">
                {filteredProviderFiles.map((file) => {
                  const importKey = `${selectedProvider.id}:${file.id}`;
                  return (
                    <div key={file.id} className="px-4 py-3 flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 truncate">{file.name}</div>
                        <div className="text-xs text-gray-500">
                          {formatDate(file.modifiedTime)}
                        </div>
                      </div>
                      <button
                        onClick={() => handleProviderImport(selectedProvider, file)}
                        disabled={importingId === importKey}
                        className="shrink-0 px-3 py-2 rounded-md bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60"
                      >
                        {importingId === importKey ? t('landing.importing') : t('landing.importFile')}
                      </button>
                    </div>
                  );
                })}
                {filteredProviderFiles.length === 0 && (
                  <div className="p-6 text-center text-sm text-gray-500">
                    {t('landing.noFilesMatchSearch')}
                  </div>
                )}
              </div>
            )}
          </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportProjectModal;
